// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React from 'react';
import { X, Crown, Star, Zap, Search, Camera, Bell, Gift } from 'lucide-react';
import useFocusTrap from '../hooks/useFocusTrap';

/**
 * Founder Welcome Modal
 * Shown to VIP Founders and Early Founders on first login per session.
 * Gives a quick overview of their benefits and how to get started.
 */
const FounderWelcomeModal = ({ onClose, userName, tier }) => {
  const trapRef = useFocusTrap({ active: true, onEscape: onClose });

  const isVIP = tier === 'VIP_FOUNDER' || tier === 'vip_founder';

  const tierConfig = isVIP
    ? {
        name: 'VIP Founder',
        gradient: 'from-purple-600 to-pink-600',
        badge: '💎',
        tagline: "You're one of our first 200 supporters",
        price: '£1.00/mo',
        buttonGradient: 'from-purple-500 to-pink-600',
        buttonHover: 'hover:from-purple-600 hover:to-pink-700',
      }
    : {
        name: 'Early Founder',
        gradient: 'from-green-600 to-teal-600',
        badge: '🌟',
        tagline: "You're among our first 750 supporters",
        price: '£1.99/mo',
        buttonGradient: 'from-green-500 to-teal-600',
        buttonHover: 'hover:from-green-600 hover:to-teal-700',
      };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70 backdrop-blur-sm animate-in fade-in duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby="founder-modal-title"
    >
      <div
        ref={trapRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${tierConfig.gradient} p-6 text-white text-center relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1 transition"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
            <Crown className="w-8 h-8 text-white" />
          </div>
          <h2 id="founder-modal-title" className="text-3xl font-bold mb-1">
            Welcome, {tierConfig.name}! {tierConfig.badge}
          </h2>
          <p className="text-white/80 font-medium">{tierConfig.tagline}</p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Thank You */}
          <section className={`${isVIP ? 'bg-purple-50 border-purple-500' : 'bg-green-50 border-green-500'} border-l-4 p-4 rounded-r-xl`}>
            <h3 className={`text-lg font-bold ${isVIP ? 'text-purple-900' : 'text-green-900'} mb-2 flex items-center gap-2`}>
              <Star className="w-5 h-5" />
              Thank You for Your Support
            </h3>
            <p className={`${isVIP ? 'text-purple-800' : 'text-green-800'}`}>
              Your {tierConfig.price} contribution helps us build the UK's best grocery price comparison app.
              As a {tierConfig.name}, you get <strong>exclusive early access</strong> to every new feature.
            </p>
          </section>

          {/* What You Can Do */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              What You Can Do
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Camera className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="font-bold text-gray-800 text-sm">Scan & Compare</div>
                  <div className="text-gray-500 text-xs">Scan any barcode to compare prices across UK supermarkets</div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Search className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="font-bold text-gray-800 text-sm">Search Products</div>
                  <div className="text-gray-500 text-xs">Search by name to find the cheapest deal instantly</div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex items-start gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Bell className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-bold text-gray-800 text-sm">Price Alerts</div>
                  <div className="text-gray-500 text-xs">Get notified when your favourite products drop in price</div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="font-bold text-gray-800 text-sm">Founder Perks</div>
                  <div className="text-gray-500 text-xs">Locked-in pricing, priority features, and exclusive badges</div>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Start */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              Quick Start
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center text-sm">
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="text-2xl mb-1">1️⃣</div>
                <div className="font-bold text-gray-800">Tap Compare</div>
                <div className="text-gray-500">Search a product or barcode</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="text-2xl mb-1">2️⃣</div>
                <div className="font-bold text-gray-800">See Prices</div>
                <div className="text-gray-500">View prices across stores</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="text-2xl mb-1">3️⃣</div>
                <div className="font-bold text-gray-800">Set Alert</div>
                <div className="text-gray-500">Get notified on price drops</div>
              </div>
            </div>
          </section>

          {/* Help */}
          <section className="bg-blue-50 rounded-xl p-4 text-center">
            <h3 className="text-md font-bold text-blue-900 mb-1">Need help?</h3>
            <p className="text-blue-700 text-sm">
              Go to <strong>More {'>'} Support {'>'} Help & Support</strong> or email us at{' '}
              <strong>support@pricepalcompare.com</strong>
            </p>
          </section>
        </div>

        {/* Footer Action */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className={`w-full bg-gradient-to-r ${tierConfig.buttonGradient} text-white text-lg font-bold py-4 rounded-xl shadow-lg ${tierConfig.buttonHover} hover:scale-[1.02] transition-all transform flex items-center justify-center gap-2`}
          >
            <span>{tierConfig.badge}</span>
            Let's Go!
          </button>
        </div>
      </div>
    </div>
  );
};

export default FounderWelcomeModal;
